<script setup lang="ts">
import { ArticleUnit } from '@/generated/graphql'
import ArticleQuantity from '@/components/article/ArticleQuantity.vue'
import ArticleQuantityField from '@/components/input/ArticleQuantityField.vue'
import { computed, ref, watch } from 'vue'
import { debounce } from 'lodash'

const props = defineProps<{
  milliQuantity: number
  unit?: ArticleUnit
  loading?: boolean
}>()
const emit = defineEmits<{
  change: [milliQuantity: number]
}>()

const pending = ref<number | undefined>(props.milliQuantity)
const dirty = computed(() => pending.value != props.milliQuantity)
watch(
  () => props.milliQuantity,
  (v) => {
    if (!dirty.value) {
      pending.value = v
    }
  },
)

const steppable = computed(() => props.unit == ArticleUnit.Pieces)

function commit() {
  if (pending.value == undefined || pending.value <= 0) {
    pending.value = props.milliQuantity
    return
  }
  if (dirty.value) {
    emit('change', pending.value)
  }
}
const commitDebounced = debounce(commit, 500)

function stepBy(deltaMilli: number) {
  pending.value = Math.max(1000, (pending.value ?? props.milliQuantity) + deltaMilli)
  commitDebounced()
}
</script>

<template>
  <div v-if="steppable" class="d-flex align-center" style="gap: 2px">
    <v-btn
      density="compact"
      variant="text"
      icon="remove"
      :disabled="(pending ?? 0) <= 1000"
      @click="stepBy(-1000)"
    />
    <span class="text-no-wrap">
      <article-quantity :milli-quantity="pending" :unit="props.unit" unit-abbreviated />
    </span>
    <v-btn density="compact" variant="text" icon="add" @click="stepBy(1000)" />
  </div>
  <div v-else class="quantity-editor-field d-flex align-center">
    <article-quantity-field
      v-model="pending"
      :unit="props.unit"
      density="compact"
      :disabled="props.loading"
      @blur="commit()"
      @keydown.enter="commit()"
    />
  </div>
</template>

<style scoped lang="scss">
.quantity-editor-field :deep(.form-field) {
  margin-bottom: 0;
}
</style>
